import type { GetStaticPaths, GetStaticProps, InferGetStaticPropsType } from 'next'
import { type DehydratedState, dehydrate, QueryClient } from '@tanstack/react-query'
import { type FC, useMemo } from 'react'

import ContentDownloadPageView from 'views/Template/ContentDownloadPage'

import {
  createAllContentDownloadPagesQuery,
  createSingleContentDownloadPageQuery,
} from '@segment/api/routes/template/queries'

import { createStaticSiteBackend, StaticSiteBackendProvider } from '@segment/contexts/staticSiteBackend'

import type { AppExternalPageProps } from '@segment/next-shared/Providers'
interface PageProps {
  slug: string
}
const Page: FC<InferGetStaticPropsType<typeof getStaticProps>> = ({ slug }) => {
  const staticSiteBackend = useMemo(() => createStaticSiteBackend(), [])

  return (
    <StaticSiteBackendProvider value={staticSiteBackend}>
      <ContentDownloadPageView slug={slug} />
    </StaticSiteBackendProvider>
  )
}

export const getStaticPaths: GetStaticPaths = async () => {
  const staticSiteBackend = createStaticSiteBackend()
  const [, queryFn] = await createAllContentDownloadPagesQuery(staticSiteBackend)
  const pages = await queryFn()
  // We fetch entries with `contetPreview` set to `true` and some of them don't have a slug
  const pagesWithSlugs = pages.filter(({ slug }) => slug)

  return {
    paths: pagesWithSlugs.map(({ slug }) => ({ params: { slug } })),
    fallback: false,
  }
}

export const getStaticProps: GetStaticProps<AppExternalPageProps & PageProps> = async ({ params }) => {
  const queryClient = new QueryClient()
  const slug = String(params?.slug)
  if (process.env.ENVIRONMENT !== `staging`) {
    const staticSiteBackend = createStaticSiteBackend()

    const singleContentDownloadPage = await queryClient.fetchQuery(
      ...createSingleContentDownloadPageQuery(staticSiteBackend, slug),
      {
        staleTime: Infinity,
      }
    )

    if (!singleContentDownloadPage) {
      throw new Error(`Page with slug ${slug} not found`)
    }
  }

  const dangerousDehydratedState = dehydrate(queryClient)

  const safeDehydratedState: DehydratedState = JSON.parse(JSON.stringify(dangerousDehydratedState))

  const props: AppExternalPageProps & PageProps = {
    dehydratedState: safeDehydratedState,
    slug,
  }

  return {
    props,
  }
}

export default Page
